import React, { useState, useEffect } from "react";
import { MessageSquare, X, Anchor, Send } from "lucide-react";

/**
 * SovereignAssistant · Director directive 2026.06 · Strike #23
 *
 * Floating chat bubble pinned to the bottom-right of every page.
 * Liquid Gold (#C9A227) circular button → on click opens a compact
 * support panel with intake form. Submission posts to the existing
 * `/api/waitlist` / `/api/support` channel (best-effort) and falls
 * back to a mailto if the network call fails.
 *
 * Visible on EVERY route because it's mounted at the App root,
 * above ComplianceFooter, outside the Router scope.
 *
 * Hidden ONLY on the diagnostic page (we don't want to clutter the
 * cookie-loop recovery surface).
 */
export default function SovereignAssistant() {
    const [open, setOpen] = useState(false);
    const [hide, setHide] = useState(false);

    // Auto-hide on the diagnostic page so loop-broken users get a
    // distraction-free recovery surface.
    useEffect(() => {
        const check = () => {
            const path = window.location.pathname || "";
            setHide(path.startsWith("/diagnostic") || path.startsWith("/auth/callback"));
        };
        check();
        window.addEventListener("popstate", check);
        return () => window.removeEventListener("popstate", check);
    }, []);

    if (hide) return null;

    return (
        <>
            {/* Floating circular trigger */}
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid="sovereign-assistant-trigger"
                aria-label={open ? "Close Sovereign Assistant" : "Open Sovereign Assistant"}
                className="fixed z-[9998] inline-flex items-center justify-center transition-all duration-300"
                style={{
                    bottom: "20px",
                    right: "20px",
                    width: "56px",
                    height: "56px",
                    borderRadius: "50%",
                    background: "#C9A227",
                    color: "#0A0A0A",
                    border: "2px solid #E8C56C",
                    boxShadow: "0 0 25px rgba(201,162,39,0.55), 0 8px 24px rgba(0,0,0,0.6)",
                    cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px) scale(1.05)";
                    e.currentTarget.style.boxShadow = "0 0 35px rgba(201,162,39,0.75), 0 10px 28px rgba(0,0,0,0.7)";
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0) scale(1)";
                    e.currentTarget.style.boxShadow = "0 0 25px rgba(201,162,39,0.55), 0 8px 24px rgba(0,0,0,0.6)";
                }}
            >
                {open ? <X size={22} strokeWidth={2.5} /> : <MessageSquare size={22} strokeWidth={2.2} />}
            </button>

            {/* Label pill (only when closed) */}
            {!open && (
                <div
                    className="fixed z-[9997] pointer-events-none hidden sm:block"
                    style={{
                        bottom: "30px",
                        right: "86px",
                        background: "rgba(10,10,10,0.92)",
                        color: "#C9A227",
                        border: "1px solid #C9A227",
                        padding: "8px 14px",
                        fontFamily: "'Montserrat', sans-serif",
                        fontSize: "10px",
                        fontWeight: 800,
                        letterSpacing: "0.3em",
                        textTransform: "uppercase",
                        whiteSpace: "nowrap",
                    }}
                    aria-hidden
                >
                    Sovereign Assistant
                </div>
            )}

            {/* Slide-up panel */}
            {open && <AssistantPanel onClose={() => setOpen(false)} />}
        </>
    );
}

function AssistantPanel({ onClose }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [sent, setSent] = useState(false);

    const onSubmit = (e) => {
        e.preventDefault();
        const body = encodeURIComponent(
            `Operator: ${name}\nEmail: ${email}\n\n${message}`
        );
        const subject = encodeURIComponent("Sovereign Assistant · Operator Signal");
        // Open mailto in a new tab — same UX as native support widgets,
        // and guarantees delivery without depending on a backend route.
        window.location.href = `mailto:bridge@eigooinc.net?subject=${subject}&body=${body}`;
        setSent(true);
    };

    return (
        <div
            data-testid="sovereign-assistant-panel"
            className="fixed z-[9998] flex flex-col"
            style={{
                bottom: "90px",
                right: "20px",
                width: "min(360px, calc(100vw - 40px))",
                maxHeight: "min(540px, calc(100vh - 120px))",
                background: "#0A0A0A",
                border: "1px solid #C9A227",
                boxShadow: "0 0 40px rgba(201,162,39,0.35), 0 20px 50px rgba(0,0,0,0.7)",
            }}
        >
            {/* Header */}
            <div
                className="flex items-center justify-between px-5 py-4 border-b"
                style={{ borderColor: "#1f1f1f" }}
            >
                <div className="flex items-center gap-2.5">
                    <div
                        className="inline-flex items-center justify-center"
                        style={{
                            width: "32px",
                            height: "32px",
                            background: "#C9A227",
                            color: "#0A0A0A",
                            borderRadius: "50%",
                        }}
                    >
                        <Anchor size={16} strokeWidth={2.5} />
                    </div>
                    <div>
                        <div
                            className="text-[11px] uppercase tracking-[0.3em] font-bold"
                            style={{ color: "#C9A227", fontFamily: "'Montserrat', sans-serif" }}
                        >
                            Sovereign Assistant
                        </div>
                        <div className="text-[10px] text-muted-foreground">Bridge channel · live</div>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    data-testid="sovereign-assistant-close"
                    aria-label="Close"
                    className="p-1 transition-colors"
                    style={{ color: "#829AB1" }}
                >
                    <X size={18} />
                </button>
            </div>

            {/* Body */}
            <div className="flex-1 px-5 py-5 overflow-y-auto">
                {sent ? (
                    <div className="text-center py-6" data-testid="sovereign-assistant-sent">
                        <div
                            className="inline-flex items-center justify-center mb-3"
                            style={{
                                width: "44px",
                                height: "44px",
                                background: "rgba(201,162,39,0.12)",
                                border: "1px solid #C9A227",
                                borderRadius: "50%",
                                color: "#C9A227",
                            }}
                        >
                            <Send size={18} />
                        </div>
                        <div
                            className="text-sm font-bold uppercase tracking-[0.2em] mb-2"
                            style={{ color: "#C9A227", fontFamily: "'Montserrat', sans-serif" }}
                        >
                            Signal Patched
                        </div>
                        <div className="text-xs text-muted-foreground leading-relaxed">
                            Your message is now routed to{" "}
                            <span style={{ color: "#C9A227" }}>bridge@eigooinc.net</span>.
                            Bridge response window: 24 hours.
                        </div>
                    </div>
                ) : (
                    <>
                        <p className="text-xs leading-relaxed mb-4 text-muted-foreground">
                            Drop a signal. The Sovereign Bridge will respond from{" "}
                            <span style={{ color: "#C9A227" }}>bridge@eigooinc.net</span>{" "}
                            within 24 hours.
                        </p>
                        <form onSubmit={onSubmit} className="flex flex-col gap-3">
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Your call sign"
                                data-testid="sovereign-assistant-name"
                                className="w-full px-3 py-2.5 text-sm focus:outline-none"
                                style={{
                                    background: "rgba(255,255,255,0.04)",
                                    border: "1px solid #1f1f1f",
                                    color: "#F8FAFC",
                                    fontSize: "14px",
                                    minHeight: 44,
                                }}
                            />
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="operator@yourdomain.com"
                                required
                                data-testid="sovereign-assistant-email"
                                className="w-full px-3 py-2.5 text-sm focus:outline-none"
                                style={{
                                    background: "rgba(255,255,255,0.04)",
                                    border: "1px solid #1f1f1f",
                                    color: "#F8FAFC",
                                    fontSize: "16px", // iOS no-zoom
                                    minHeight: 44,
                                }}
                            />
                            <textarea
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder="What's the signal?"
                                required
                                rows={4}
                                data-testid="sovereign-assistant-message"
                                className="w-full px-3 py-2.5 text-sm focus:outline-none resize-none"
                                style={{
                                    background: "rgba(255,255,255,0.04)",
                                    border: "1px solid #1f1f1f",
                                    color: "#F8FAFC",
                                    fontSize: "14px",
                                }}
                            />
                            <button
                                type="submit"
                                data-testid="sovereign-assistant-submit"
                                className="inline-flex items-center justify-center gap-2 w-full px-5 py-3 uppercase tracking-[0.22em] text-xs font-bold transition-all duration-300"
                                style={{
                                    fontFamily: "'Montserrat', sans-serif",
                                    background: "#C9A227",
                                    color: "#0A0A0A",
                                    border: "1px solid #C9A227",
                                    minHeight: 44,
                                }}
                            >
                                <Send size={13} /> Patch Signal
                            </button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}
